import React from 'react'

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}

function isStandalone(): boolean {
  // iOS Safari uses navigator.standalone; others via display-mode
  return (
    (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
    (typeof (navigator as any).standalone === 'boolean' && (navigator as any).standalone)
  )
}

function isiOSSafari(): boolean {
  const ua = navigator.userAgent || ''
  const isIOS = /iPhone|iPad|iPod/.test(ua)
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua)
  return isIOS && isSafari
}

function snoozedUntil(): number {
  try { return Number(localStorage.getItem('pt_install_snooze_until') || '0') } catch { return 0 }
}

function setSnooze(days: number) {
  try { localStorage.setItem('pt_install_snooze_until', String(Date.now() + days*24*60*60*1000)) } catch { /* ignore */ }
}

export function InstallPrompt() {
  const [deferred, setDeferred] = React.useState<BeforeInstallPromptEvent | null>(null)
  const [showIosGuide, setShowIosGuide] = React.useState(false)
  const [eligible, setEligible] = React.useState(false)

  React.useEffect(() => {
    if (isStandalone() || Date.now() < snoozedUntil()) return

    let hasPrompt = false
    const onBIP = (e: Event) => {
      e.preventDefault()
      hasPrompt = true
      setDeferred(e as BeforeInstallPromptEvent)
      setEligible(true)
    }
    window.addEventListener('beforeinstallprompt', onBIP as any)

    // If no beforeinstallprompt and on iOS Safari, show guide option instead
    const ios = isiOSSafari()
    const t = setTimeout(() => {
      if (!hasPrompt && ios) setEligible(true)
    }, 300)

    return () => { window.removeEventListener('beforeinstallprompt', onBIP as any); clearTimeout(t) }
  }, [])

  if (!eligible || isStandalone() || Date.now() < snoozedUntil()) return null

  const handleInstall = async () => {
    if (deferred) {
      try {
        await deferred.prompt()
        const choice = await deferred.userChoice
        if (choice.outcome === 'accepted') {
          setEligible(false)
        } else {
          setSnooze(3) // try again later
          setEligible(false)
        }
      } catch { /* ignore */ }
      setDeferred(null)
    } else if (isiOSSafari()) {
      setShowIosGuide(true)
    } else {
      // Not supported; hide for a week
      setSnooze(7)
      setEligible(false)
    }
  }

  return (
    <>
      <button className="hud-chip" style={{marginRight:8}} onClick={handleInstall} title="Install app">
        Install app
      </button>
      <button className="hud-chip" onClick={()=>{ setSnooze(7); setEligible(false) }} title="Hide for now">Later</button>
      {showIosGuide && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="modal" style={{width:'min(92vw, 520px)'}}>
            <div className="modal-header">
              <div>Installeren op iOS</div>
              <button className="btn btn-ghost" onClick={()=>setShowIosGuide(false)}>Close</button>
            </div>
            <div className="modal-body">
              <div className="list">
                <div className="list-item" style={{display:'grid', gap:6}}>
                  <div>Voeg PushTrack toe aan je beginscherm voor een schermvullende ervaring:</div>
                  <ol style={{margin:'6px 0 0 18px'}}>
                    <li>Open het deel-menu (Share) in Safari</li>
                    <li>Kies “Add to Home Screen” (Zet op beginscherm)</li>
                    <li>Open de app vanuit je beginscherm</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
